import React from 'react';
import { getApiBaseFromWindow } from '../../../lib/runtimeConfig';
import { useTranslation } from 'react-i18next';

import type {
  WorldRoom,
} from '../../../types/colyseus';

import type {
  FollowManager,
} from '../../../game/followManager';

import type {
  GameBridge,
} from '../../../types/game';

import {
  getSpacePlayerPresentation,
} from '../../../spaceWork/teamProfiles';


interface WorldContextMenuProps {

  contextMenu: {
    open: boolean;
    x: number;
    y: number;
    playerId: string | null;
  };

  onClose: () => void;

  localPosRef:
    React.RefObject<{
      id: string;
      x?: number;
      y?: number;
    }>;

  bubbleGroupsRef:
    React.RefObject<
      Record<string, string>
    >;

  followRef:
    React.RefObject<
      FollowManager | null
    >;

  gameBridge:
    GameBridge;

  colyseusRef:
    React.RefObject<
      WorldRoom | null
    >;

  bubbleStartRef:
    React.RefObject<
      null | ((id: string) => void)
    >;

  resolvePlayerName:
    (playerId: string) => string;
}


function getMembersOfGroup(
  groupId: string,
  bubbleGroups:
    Record<string, string>,
): string[] {

  return Object
    .entries(bubbleGroups)
    .filter(
      ([, gid]) =>
        gid === groupId,
    )
    .map(([sid]) => sid);

}


function buildContextMenuActions(
  props: WorldContextMenuProps,
) {

  const {
    contextMenu,
    onClose,
    localPosRef,
    bubbleGroupsRef,
    followRef,
    gameBridge,
    colyseusRef,
    bubbleStartRef,
  } = props;


  const id =
    contextMenu.playerId!;


  const isLocal =
    id === 'local';


  const handleFollowClick = () => {

    onClose();

    if (isLocal) return;


    if (
      followRef.current
        ?.getTarget?.() === id
    ) {

      followRef.current.stop();

      gameBridge
        .setDesiredPosition(null);

    } else {

      followRef.current
        ?.startFollowing?.(id);

    }

  };


  const handleJoinBubbleClick = () => {

    onClose();

    if (isLocal) return;


    try {

      const targetGroup =
        bubbleGroupsRef
          .current?.[id];

      const meId =
        localPosRef
          .current?.id;


      if (
        !targetGroup ||
        !meId
      ) return;


      const currentMembers =
        getMembersOfGroup(
          targetGroup,
          bubbleGroupsRef.current || {},
        );


      const next =
        Array.from(
          new Set([
            ...currentMembers,
            meId,
          ]),
        );


      colyseusRef.current
        ?.send?.(
          'bubble_update',
          {
            id: targetGroup,
            members: next,
          },
        );

    } catch {}

  };


  const handleAddToBubbleClick = () => {

    onClose();

    if (isLocal) return;


    try {

      const meId =
        localPosRef.current?.id;

      if (!meId) return;


      const myGroup =
        bubbleGroupsRef
          .current?.[meId];


      if (!myGroup) return;


      const currentMembers =
        getMembersOfGroup(
          myGroup,
          bubbleGroupsRef.current || {},
        );


      const next =
        Array.from(
          new Set([
            ...currentMembers,
            id,
          ]),
        );


      colyseusRef.current
        ?.send?.(
          'bubble_update',
          {
            id: myGroup,
            members: next,
          },
        );

    } catch {}

  };


  const handleStartBubbleClick = () => {

    onClose();

    if (isLocal) return;

    bubbleStartRef
      .current?.(id);

  };


  const shouldShowJoinBubble = (() => {

    if (isLocal) return false;

    try {

      const targetGroup =
        bubbleGroupsRef
          .current?.[id] || null;

      const meId =
        localPosRef.current?.id;

      const myGroup =
        meId
          ? bubbleGroupsRef
              .current?.[meId] || null
          : null;


      return (
        !!targetGroup &&
        targetGroup !== myGroup
      );

    } catch {

      return false;

    }

  })();


  const shouldShowAddToBubble = (() => {

    if (isLocal) return false;

    try {

      const meId =
        localPosRef.current?.id;

      const myGroup =
        meId
          ? bubbleGroupsRef
              .current?.[meId] || null
          : null;


      return !!myGroup;

    } catch {

      return false;

    }

  })();


  return {
    isLocal,
    handleFollowClick,
    handleJoinBubbleClick,
    handleAddToBubbleClick,
    handleStartBubbleClick,
    shouldShowJoinBubble,
    shouldShowAddToBubble,
  };

}


function MetricRow(
  props: {
    icon: string;
    label: string;
    value: string;
  },
) {

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns:
          '22px 1fr auto',
        gap: 8,
        alignItems: 'center',

        padding:
          '7px 0',

        borderBottom:
          '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span
        style={{
          fontSize: 14,
        }}
      >
        {props.icon}
      </span>

      <span
        style={{
          color:
            'rgba(255,255,255,0.66)',

          fontSize: 12,
        }}
      >
        {props.label}
      </span>

      <strong
        style={{
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {props.value}
      </strong>
    </div>
  );

}



type SpaceWorkMetrics = {
  salesToday: number;
  revenueToday: number;

  salesWeek: number;
  revenueWeek: number;

  salesMonth: number;
  revenueMonth: number;

  agendaToday: number;
  agendaWeek: number;
  agendaMonth: number;

  meetingsRealizedToday:
    number | null;

  meetingsRealizedWeek:
    number | null;

  meetingsRealizedMonth:
    number | null;

  noShowsToday:
    number | null;

  noShowsWeek:
    number | null;

  noShowsMonth:
    number | null;

  conversionWeek:
    number | null;

  weeklyGoal: number;

  weeklyGoalRemaining:
    number;

  weeklyGoalProgress:
    number | null;
};


function formatMoney(
  value: number,
) {

  return Number(
    value || 0
  ).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  );
}


function formatPercent(
  value: number,
) {

  return Number(
    value || 0
  ).toLocaleString(
    'pt-BR',
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    },
  );
}


function OperationalMetrics(
  {
    role,
    rawName,
  }: {
    role: string;
    rawName: string;
  },
) {

  const [
    metrics,
    setMetrics,
  ] = React.useState<
    SpaceWorkMetrics | null
  >(null);


  const [
    loading,
    setLoading,
  ] = React.useState(
    role === 'Closer',
  );


  const [
    failed,
    setFailed,
  ] = React.useState(false);


  React.useEffect(
    () => {

      if (
        role !== 'Closer'
      ) {
        return;
      }


      let mounted = true;


      const load =
        async () => {

          const controller =
            new AbortController();


          const requestTimeout =
            window.setTimeout(
              () => {
                controller.abort();
              },
              10_000,
            );


          try {

            const apiBase =
              getApiBaseFromWindow();


            const url =
              `${apiBase}/space-work/metrics`
              + `?name=${encodeURIComponent(rawName)}`
              + `&role=Closer`;


            const response =
              await fetch(
                url,
                {
                  credentials:
                    'include',

                  signal:
                    controller.signal,
                },
              );


            const payload =
              await response.json();


            if (
              !response.ok
              || !payload?.metrics
            ) {

              throw new Error(
                `HTTP ${response.status}`
              );
            }


            if (!mounted) {
              return;
            }


            
setMetrics(
  payload.metrics as SpaceWorkMetrics
);


            setFailed(false);

          } catch {

            if (mounted) {
              setFailed(true);
            }

          } finally {

            window.clearTimeout(
              requestTimeout
            );


            if (mounted) {
              setLoading(false);
            }
          }
        };


      void load();


      const timer =
        window.setInterval(
          () => {
            void load();
          },
          30_000,
        );


      return () => {

        mounted = false;

        window.clearInterval(
          timer,
        );
      };

    },
    [
      role,
      rawName,
    ],
  );


  if (
    role === 'Closer'
  ) {

    if (
      loading
      && !metrics
    ) {

      return (
        <MetricRow
          icon="⏳"
          label="Métricas"
          value="Carregando..."
        />
      );
    }


    if (
      failed
      && !metrics
    ) {

      return (
        <MetricRow
          icon="⚠️"
          label="Métricas"
          value="Indisponíveis"
        />
      );
    }


    if (!metrics) {

      return (
        <MetricRow
          icon="⚠️"
          label="Métricas"
          value="—"
        />
      );
    }


    const conversion =
      metrics.conversionWeek
        === null

        ? '—'

        : (
            `${formatPercent(
              metrics.conversionWeek
            )}%`
            +
            (
              metrics
                .meetingsRealizedWeek
                !== null

                ? ` (${metrics.salesWeek}/${metrics.meetingsRealizedWeek})`

                : ''
            )
          );


    const realizedWeek =
      formatMoney(
        metrics.revenueWeek
      )
      +
      (
        metrics
          .weeklyGoalProgress
          !== null

          ? ` (${metrics.weeklyGoalProgress}%)`

          : ''
      );


    return (
      <>

        <MetricRow
          icon="💰"
          label="Vendas hoje"
          value={
            String(
              metrics.salesToday
            )
          }
        />

        <MetricRow
          icon="💵"
          label="Vendido hoje"
          value={
            formatMoney(
              metrics.revenueToday
            )
          }
        />


        <MetricRow
          icon="📅"
          label="Agenda hoje"
          value={
            String(
              metrics.agendaToday
            )
          }
        />

        <MetricRow
          icon="🤝"
          label="Realizadas hoje"
          value={
            metrics
              .meetingsRealizedToday
              === null

              ? '—'

              : String(
                  metrics
                    .meetingsRealizedToday
                )
          }
        />


        {
          metrics.noShowsToday
            !== null

            ? (
                <MetricRow
                  icon="👻"
                  label="No-shows hoje"
                  value={
                    String(
                      metrics
                        .noShowsToday
                    )
                  }
                />
              )

            : null
        }


        <MetricRow
          icon="📈"
          label="Conversão semana"
          value={conversion}
        />


        <MetricRow
          icon="🎯"
          label="Meta semanal"
          value={
            formatMoney(
              metrics.weeklyGoal
            )
          }
        />

        <MetricRow
          icon="🚀"
          label="Realizado semana"
          value={realizedWeek}
        />

        <MetricRow
          icon="📌"
          label="Falta para meta"
          value={
            formatMoney(
              metrics
                .weeklyGoalRemaining
            )
          }
        />


        <MetricRow
          icon="📊"
          label="Vendas no mês"
          value={
            String(
              metrics.salesMonth
            )
          }
        />

        <MetricRow
          icon="💰"
          label="Vendido no mês"
          value={
            formatMoney(
              metrics.revenueMonth
            )
          }
        />

      </>
    );
  }


  if (
    role === 'SDR'
  ) {

    return (
      <>

        <MetricRow
          icon="📞"
          label="Ligações hoje"
          value="—"
        />

        <MetricRow
          icon="💬"
          label="Atendidas"
          value="—"
        />

        <MetricRow
          icon="📅"
          label="Agendamentos"
          value="—"
        />

        <MetricRow
          icon="🔥"
          label="Leads quentes"
          value="—"
        />

      </>
    );
  }


  return (
    <MetricRow
      icon="⚡"
      label="Status"
      value="Ativo"
    />
  );
}


function ActionButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',

        padding:
          '8px 10px',

        border:
          '1px solid rgba(255,255,255,0.10)',

        borderRadius: 7,

        background:
          'rgba(255,255,255,0.04)',

        color: '#fff',

        textAlign: 'left',

        fontSize: 12,

        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );

}


export function WorldContextMenu(
  props: WorldContextMenuProps,
) {

  const {
    contextMenu,
    onClose,
    resolvePlayerName,
  } = props;


  const { t } =
    useTranslation();


  if (
    !contextMenu.open ||
    !contextMenu.playerId
  ) return null;


  const actions =
    buildContextMenuActions(props);


  const rawName =
    resolvePlayerName(
      contextMenu.playerId,
    );


  const presentation =
    getSpacePlayerPresentation(
      rawName,
    );


  const role =
    presentation.profile.role;


  const cardWidth = 292;
  const estimatedHeight =
    role === 'SDR'
      ? 390
      : 360;


  return (
    <div
      role="button"
      tabIndex={0}

      onClick={onClose}

      onKeyDown={(e) => {

        if (
          e.key === 'Escape'
        ) {
          onClose();
        }

      }}

      onContextMenu={(e) =>
        e.preventDefault()
      }

      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
      }}
    >

      <div
        role="dialog"
        tabIndex={0}

        onClick={(e) =>
          e.stopPropagation()
        }

        onKeyDown={(e) => {

          if (
            e.key === 'Escape'
          ) {

            e.stopPropagation();
            onClose();

          }

        }}

        style={{
          position: 'absolute',

          left:
            Math.min(
              Math.max(
                8,
                contextMenu.x,
              ),
              window.innerWidth -
                cardWidth -
                8,
            ),

          top:
            Math.min(
              Math.max(
                8,
                contextMenu.y,
              ),
              window.innerHeight -
                estimatedHeight -
                8,
            ),

          width: cardWidth,

          padding: 14,

          background:
            'rgba(13,13,15,0.97)',

          color: '#fff',

          border:
            '1px solid rgba(255,255,255,0.12)',

          borderRadius: 14,

          boxShadow:
            '0 18px 60px rgba(0,0,0,0.55)',

          backdropFilter:
            'blur(14px)',
        }}
      >

        {/* HEADER */}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              'space-between',
            gap: 12,
          }}
        >

          <div>

            <div
              style={{
                fontSize: 17,
                fontWeight: 750,
                lineHeight: 1.1,
              }}
            >
              {
                presentation
                  .displayName
              }
            </div>

            <div
              style={{
                marginTop: 5,

                color:
                  'rgba(255,255,255,0.64)',

                fontSize: 11,
              }}
            >
              {role}
              {' • '}
              <span
                style={{
                  color: '#67d391',
                }}
              >
                ● Online
              </span>
            </div>

          </div>


          <button
            type="button"
            onClick={onClose}

            style={{
              width: 28,
              height: 28,

              borderRadius: 8,

              border:
                '1px solid rgba(255,255,255,0.08)',

              background:
                'rgba(255,255,255,0.04)',

              color:
                'rgba(255,255,255,0.65)',

              cursor: 'pointer',
            }}
          >
            ×
          </button>

        </div>


        {/* STATUS */}

        <div
          style={{
            marginTop: 14,
            marginBottom: 8,

            padding:
              '8px 10px',

            borderRadius: 8,

            background:
              'rgba(255,255,255,0.035)',

            color:
              'rgba(255,255,255,0.72)',

            fontSize: 11,
          }}
        >
          🟢 Disponível agora
        </div>


        {/* METRICAS */}

        <div
          style={{
            marginTop: 6,
          }}
        >
          <OperationalMetrics
            role={role}
            rawName={rawName}
          />
        </div>


        {/* AVISO ENQUANTO INTEGRACAO AINDA NAO ESTA LIGADA */}


        {/* ACOES DO MEETROPOLIS */}

        {!actions.isLocal && (

          <div
            style={{
              display: 'grid',
              gap: 6,
              marginTop: 14,
            }}
          >

            <ActionButton
              onClick={
                actions
                  .handleFollowClick
              }
            >
              👣 {
                t(
                  'worldContextMenu.follow',
                )
              }
            </ActionButton>


            {
              actions
                .shouldShowJoinBubble &&
              (
                <ActionButton
                  onClick={
                    actions
                      .handleJoinBubbleClick
                  }
                >
                  💬 {
                    t(
                      'worldContextMenu.joinBubble',
                    )
                  }
                </ActionButton>
              )
            }


            {
              actions
                .shouldShowAddToBubble &&
              (
                <ActionButton
                  onClick={
                    actions
                      .handleAddToBubbleClick
                  }
                >
                  ➕ {
                    t(
                      'worldContextMenu.addToBubble',
                    )
                  }
                </ActionButton>
              )
            }


            <ActionButton
              onClick={
                actions
                  .handleStartBubbleClick
              }
            >
              🎙 {
                t(
                  'worldContextMenu.startBubble',
                )
              }
            </ActionButton>

          </div>

        )}

      </div>

    </div>
  );

}
